import { routerNavigatedAction } from '@ngrx/router-store';
import { createReducer, on } from '@ngrx/store';
import { createEmptyStateResource, createStateResource, StateResource } from '@schir-int-client/ngrx-helpers';
import { sameUri } from '@schir-int-client/tech';
import { setKontaktAssignmentSuccessAction } from 'libs/adressverwaltung-shared/src/lib/adressverwaltung.actions';
import {
	downloadAmtlicherAuszugAction,
	downloadAmtlicherAuszugSuccessAction,
	getLastOpenedVerfahrenAction,
	loadVerfahrenAction,
	loadVerfahrenSingleSuccessAction,
	loadVerfahrenSuccessAction,
	loadZaehlblattSuccessAction,
	searchForVerfahrenAction,
	searchForVerfahrenSuccessAction,
	unassignKontaktSuccessAction,
	updateVerfahrenSuccessAction,
} from './verfahren.actions';
import {
	VerfahrenListResource,
	VerfahrenResource,
	VerfahrenSearchQuery,
	VerfahrenStatus,
	ZaehlblattEntryListResource,
} from './verfahren.model';
import { VerfahrenSearchModeLabel } from './verfahren.messages';
import { getEmbeddedResource } from '@ngxp/rest';
import { VerfahrenListLinkRel } from './verfahren.linkrel';
import { isNil } from 'lodash-es';

export interface VerfahrenState {
	verfahren: StateResource<VerfahrenListResource>;
	verfahrenSingle: VerfahrenResource;
	searchQuery: VerfahrenSearchQuery;
	downloadAmtlicherAuszugDone: StateResource<boolean>;
	zaehlblatt: StateResource<ZaehlblattEntryListResource>;
}

export const initialState: VerfahrenState = {
	verfahren: createEmptyStateResource(),
	verfahrenSingle: null,
	searchQuery: {
		searchString: null,
		searchIn: Object.keys(VerfahrenSearchModeLabel),
		filterByStatus: Object.keys(VerfahrenStatus).map(k => VerfahrenStatus[k]),
	},
	downloadAmtlicherAuszugDone: createEmptyStateResource(),
	zaehlblatt: createEmptyStateResource(),
};

export const verfahrenReducer = createReducer(
	initialState,
	on(downloadAmtlicherAuszugAction, (state: VerfahrenState): VerfahrenState => ({
		...state,
		downloadAmtlicherAuszugDone: createEmptyStateResource(true),
	})),
	on(downloadAmtlicherAuszugSuccessAction, (state: VerfahrenState): VerfahrenState => ({
		...state,
		downloadAmtlicherAuszugDone: createStateResource(true),
	})),
	on(loadVerfahrenAction, (state: VerfahrenState): VerfahrenState => ({
		...state,
		verfahren: createEmptyStateResource(true),
	})),
	on(loadVerfahrenSuccessAction, (state: VerfahrenState, { verfahrenList }): VerfahrenState => ({
		...state,
		verfahren: createStateResource(verfahrenList),
		verfahrenSingle: getFirstVerfahrenInList(verfahrenList),
	})),
	on(updateVerfahrenSuccessAction, (state: VerfahrenState, { verfahren }): VerfahrenState => ({
		...state,
		verfahren: { ...state.verfahren, reload: true },
		verfahrenSingle: sameUri(state.verfahrenSingle, verfahren) ? verfahren : state.verfahrenSingle,
	})),
	on(loadVerfahrenSingleSuccessAction, (state: VerfahrenState, { verfahren }): VerfahrenState => ({
		...state,
		verfahrenSingle: verfahren,
		zaehlblatt: createEmptyStateResource(),
	})),
	on(loadZaehlblattSuccessAction, (state: VerfahrenState, { zaehlblatt }): VerfahrenState => ({
		...state,
		zaehlblatt: createStateResource(zaehlblatt),
	})),
	on(searchForVerfahrenAction, (state: VerfahrenState, { query }): VerfahrenState => ({
		...state,
		verfahren: createEmptyStateResource(true),
		searchQuery: query,
	})),
	on(getLastOpenedVerfahrenAction, (state: VerfahrenState, { query }): VerfahrenState => ({
		...state,
		verfahren: createEmptyStateResource(true),
		searchQuery: query,
	})),
	on(
		searchForVerfahrenSuccessAction,
		(state: VerfahrenState, { verfahrenList, query, keepVerfahrenSingle }): VerfahrenState => {
			if (state.searchQuery && state.searchQuery !== query) {
				console.log('Warning: Ignoring search result which does not match current query');
				return state;
			}
			let firstVerfahren = keepVerfahrenSingle ? state.verfahrenSingle : getFirstVerfahrenInList(verfahrenList);
			if (!isNil(firstVerfahren)) {
				firstVerfahren = {
					...firstVerfahren,
					implicitlySpecified: true,
				};
			}
			return {
				...state,
				verfahren: createStateResource(verfahrenList),
				verfahrenSingle: firstVerfahren,
			};
		}
	),
	on(setKontaktAssignmentSuccessAction, (state: VerfahrenState, { verfahren }): VerfahrenState => ({
		...state,
		verfahren: { ...state.verfahren, reload: true },
		verfahrenSingle: verfahren,
	})),
	on(unassignKontaktSuccessAction, (state: VerfahrenState, { verfahren }): VerfahrenState => ({
		...state,
		verfahren: { ...state.verfahren, reload: true },
		verfahrenSingle: verfahren,
	})),
	on(routerNavigatedAction, (state: VerfahrenState, { payload }): VerfahrenState => {
		if (payload.event.url.startsWith('/verfahrenDetail/')) {
			return { ...initialState, searchQuery: state.searchQuery, verfahren: createEmptyStateResource() };
		} else if (payload.event.url.startsWith('/verfahren/')) {
			return { ...state, verfahren: createEmptyStateResource() };
		}
		return payload.event.url.startsWith('/verfahren') ? state : initialState;
	})
);

function getFirstVerfahrenInList(verfahrenList: VerfahrenListResource) {
	return getEmbeddedResource(verfahrenList, VerfahrenListLinkRel.VERFAHREN_LIST)?.[0] ?? null;
}
